/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { authenticatedFetch } from '@shared/lib/api';
import { handleApiResponse } from '@shared/lib/apiResponse';
import {
  AgentsListResponseData,
  AgentResponseData,
  AgentExecutionResponseData,
  CreateAgentResponseData
} from '@shared/types/responses/agents';
import {
  ListAgentsRequest,
  CreateAgentRequest
} from '@shared/types/requests/agents';

/**
 * List available agents
 */
export async function listAgents(filters?: Partial<ListAgentsRequest>): Promise<AgentsListResponseData> {
  const params = new URLSearchParams();
  
  if (filters) {
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        params.append(key, String(value));
      }
    });
  }
  
  const url = params.toString() ? `/api/agent/agents?${params.toString()}` : '/api/agent/agents';
  const response = await authenticatedFetch(url);
  return handleApiResponse<AgentsListResponseData>(response);
}

/**
 * Get agent by ID
 */
export async function getAgent(id: number): Promise<AgentResponseData> {
  const response = await authenticatedFetch(`/api/agent/agents/${id}`);
  return handleApiResponse<AgentResponseData>(response);
}

/**
 * Get agent by name
 */
export async function getAgentByName(name: string): Promise<AgentResponseData> {
  const response = await authenticatedFetch(`/api/agent/agents/by-name/${name}`);
  return handleApiResponse<AgentResponseData>(response);
}

/**
 * Create a new agent
 */
export async function createAgent(data: CreateAgentRequest): Promise<CreateAgentResponseData> {
  const response = await authenticatedFetch('/api/agent/agents', {
    method: 'POST',
    body: data
  });
  return handleApiResponse<CreateAgentResponseData>(response);
}

/**
 * Update an agent (for system-level changes)
 */
export async function updateAgent(id: number, data: any): Promise<AgentResponseData> {
  const response = await authenticatedFetch(`/api/agent/agents/${id}`, {
    method: 'PUT',
    body: data
  });
  return handleApiResponse<AgentResponseData>(response);
}

/**
 * Update agent sidebar visibility preference
 */
export async function updateAgentSidebarPreference(agentId: number, isEnabledInSidebar: boolean): Promise<any> {
  const response = await authenticatedFetch('/api/settings/users/agent-sidebar', {
    method: 'PUT',
    body: { agentId, isEnabledInSidebar }
  });
  return handleApiResponse(response);
}

/**
 * Update multiple agents' sidebar visibility preferences (bulk operation)
 */
export async function updateBulkAgentSidebarPreference(agentIds: number[], isEnabledInSidebar: boolean): Promise<any> {
  const response = await authenticatedFetch('/api/settings/users/agent-sidebar/bulk', {
    method: 'PUT',
    body: { agentIds, isEnabledInSidebar }
  });
  return handleApiResponse(response);
}


